import WooriImg1 from '@/assets/img/corporate-and-organization/woori_img01@2x.jpg';
import WooriLogo from '@/assets/img/corporate-and-organization/woori_logo@2x.png';
import WooriLogoM from '@/assets/img/corporate-and-organization/woori_logo_m@2x.png';
import Button from '@/components/Button';
import Container from '@/components/Container';
import Image from '@/components/Image';
import LinkSafe from '@/components/LinkSafe';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import { navigate, PageProps } from 'gatsby';

import React from 'react';
import styled from 'styled-components';

const Flex = styled.div`
  display: flex;
`;
const ImgBox = styled.div`
  position: relative;
  width: 100%;

  .img_copyright {
    font-size: 12px;
    color: #fff;
    opacity: 0.5;
    position: absolute;
    bottom: 23px;
    left: 20px;
    text-shadow: 0px 0px 10px rgba(0, 0, 0, 0.8);

    ${breakpoint(`mobile`)} {
      bottom: 13px;
      left: 10px;
    }
  }
`;
const SectionHead = styled.section`
  background: #f8fcfe;
  padding: 96px 0 120px;

  ${breakpoint(`mobile`)} {
    padding: 72px 0 44px;

    .br_pc {
      display: none;
    }
  }

  ${Flex} {
    align-items: flex-end;
    gap: 60px;

    ${breakpoint(`tablet`)} {
      flex-direction: column;
    }
    ${breakpoint(`mobile`)} {
      gap: 32px;
    }
  }

  ${ImgBox} {
    width: 57%;
    ${breakpoint(`tablet`)} {
      width: 100%;
    }
  }

  .company_info {
    ${breakpoint(`tablet`)} {
      width: 100%;
    }
    .company_logo img {
      width: auto;
    }

    .company_name {
      font-size: 40px;
      font-weight: 700;
      margin: 30px 0 40px;
    }

    ul {
      border-top: 1px solid #2d2926;
      padding-top: 20px;

      li {
        display: flex;
        align-items: center;
        position: relative;

        & + li {
          margin-top: 6px;
        }

        &::before {
          content: '';
          display: block;
          width: 4px;
          height: 4px;
          border-radius: 100%;
          background: #1cabe2;
          position: absolute;
          top: 11px;
          left: 0;
          flex-shrink: 0;
        }

        .info_tit {
          font-weight: 700;
          color: #1cabe2;
          width: 102px;
          flex-shrink: 0;
          margin-right: 45px;
          padding-left: 12px;
        }
      }
    }
    ${breakpoint(`mobile`)} {
      .company_name {
        font-size: 24px;
        margin: 12px 0 20px;
      }

      ul {
        li {
          line-height: 28px;
          & + li {
            margin-top: 4px;
          }

          .info_tit {
            width: 92px;
            margin-right: 30px;
          }
        }
      }
    }
  }
`;
const SectionBody = styled.section`
  padding-top: 120px;
  p {
    margin-top: 32px;
    line-height: 32px;
  }

  ${breakpoint(`mobile`)} {
    padding-top: 60px;

    .br_pc {
      display: none;
    }
    p {
      font-size: 14px;
      line-height: 28px;
    }
  }

  .btn_wrap {
    margin-top: 64px;
    text-align: center;

    ${breakpoint(`mobile`)} {
      margin-top: 48px;
    }
    ${Button} {
      font-size: 16px;
    }
  }
`;

const WooriCap: React.FC<PageProps> = ({ location }) => (
  <LayoutWithTitle
    location={location}
    title="기업·기관 사회공헌"
    description="for every child, partnerships"
  >
    <SectionHead>
      <Container>
        <Flex>
          <ImgBox>
            <img
              src={WooriImg1}
              alt="유니세프 X 카카오뱅크 글로벌 기후위기에 노출된 미래세대 지원을 위한 사회공헌 협약 이미지"
            />
            <div className="img_copyright">© 유니세프 한국위원회</div>
          </ImgBox>
          <div className="company_info">
            <div className="company_logo">
              <Image
                pcSrc={WooriLogo}
                mobileSrc={WooriLogoM}
                alt="Kakaobank 로고"
              />
            </div>
            <div className="company_name">JB우리캐피탈</div>
            <ul>
              <li>
                <div className="info_tit">산업군</div>
                <div className="info_con">금융</div>
              </li>
              <li>
                <div className="info_tit">파트너십 레벨</div>
                <div className="info_con">National</div>
              </li>
              <li>
                <div className="info_tit">홈페이지</div>
                <div className="info_con">
                  <LinkSafe to="https://www.wooricap.com/" target="_blank">
                    https://www.wooricap.com/
                  </LinkSafe>
                </div>
              </li>
              <li>
                <div className="info_tit">지원 분야​</div>
                <div className="info_con">Protection</div>
              </li>
            </ul>
          </div>
        </Flex>
      </Container>
    </SectionHead>
    <SectionBody>
      <Container>
        <Tit size="s1-5">JB우리캐피탈은</Tit>
        <p>
          2025년 4월, JB금융그룹 계열사 JB우리캐피탈과 유니세프 한국위원회는
          파트너십 협약을 맺고 국내 출생 미등록 아동 지원을 위한 ‘프로젝트 169’
          사업을 시작했습니다.
          <br />
          이번 파트너십은 <strong>‘지속가능발전목표(SDGs)’의 16.9 목표</strong>
          인 “2030년까지 모든 사람에게 법적 지위를 부여한다”를 실현하기 위한
          첫걸음으로,
          <br className="br_pc" />
          출생 미등록 아동의 권리 보호와 법적 지위 확보를 목표로 합니다.
        </p>
        <p>
          유엔아동권리협약에 따르면, 모든 아동은 출생등록을 포함한 기본 권리를
          보장받아야 합니다.
          <br />
          출생등록은 아동이 ‘인격체’로 성장할 수 있도록 하는 중요한 전제
          조건이며, 아동의 노동, 결혼, 불법 매매, 입양 등의 위험을 방지하는 데
          필수적입니다.
        </p>
        <p>
          그러나 국내에는 출생 등록이 되지 않은 아동이 2~3만 명에 달할 것으로
          추산됩니다.
          <br />
          이로 인해 출생 미등록 아동은 법적 보호를 받지 못하고 의료 및 교육
          서비스도 제대로 제공받지 못하는 상황에 놓여 있습니다.
        </p>
        <p>
          JB우리캐피탈과 유니세프 한국위원회는{' '}
          <strong>
            ‘프로젝트 169’를 통해 시흥시에서 출생 미등록 아동 지원
          </strong>
          을 위한 여러 사업을 진행하고 있습니다.
          <br />
          여기에는 영유아 건강검진비, 의료비 및 약제비 지원 등이 포함되며,{' '}
          <strong>JB우리캐피탈의 금융 교육</strong>을 비롯한 다양한 교육을 통해
          보호자들에게 실질적인 지원을 제공할 예정입니다.
          <br />
          또한, ‘프로젝트 169’는 연내 화성시로도 확대될 예정입니다.
        </p>
        <p>
          이번 파트너십은 기업, 지자체, 비영리 기관이 함께 협력하는{' '}
          <strong>지속 가능한 지원 모델</strong>로서, 지역사회의 자원이 연계되어
          더욱 큰 시너지를 낼 것입니다.
        </p>
        <p>
          JB우리캐피탈과 유니세프 한국위원회는 앞으로도 출생 미등록 아동의 법적
          지위 보장과 생활 환경 개선을 위해 더욱 다양한 사업을 전개하며,
          <br className="br_pc" />
          이를 바탕으로 정책적 해결 방안을 제시하고,{' '}
          <strong>출생 미등록 아동의 기본권을 보장</strong>하는 데 기여할
          예정입니다.
        </p>
      </Container>

      <div className="btn_wrap">
        <Button
          className="btn"
          color="grey"
          width={256}
          onClick={() =>
            navigate(`/involve/corporate-and-organization/social-contribution`)
          }
        >
          뒤로가기
        </Button>
      </div>
    </SectionBody>
  </LayoutWithTitle>
);

export default WooriCap;
